import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Search, Pencil, Plus, Trash2, Save, X, BookOpen, Maximize2, Minimize2,
  PanelLeftClose, PanelLeftOpen, Type,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { docsApi } from '../../services/api';
import Mermaid from './Mermaid';
import { useAuthStore } from '../../store/useAuthStore';

const CATEGORY_LABEL: Record<string, string> = { guide: 'User Guides', reference: 'API Reference', concept: 'Concepts' };
const CATEGORY_ORDER = ['guide', 'reference', 'concept'];

/** Reading width. A full-bleed line of prose on a wide monitor is unreadable —
 *  the eye loses the start of the next line — so expanding the reader has to
 *  come with a way to constrain the measure. Persisted, because it is a
 *  per-person preference about eyesight and monitor, not a per-document one. */
const READ_WIDTHS = {
  narrow: { label: 'Narrow', cls: 'max-w-2xl' },
  medium: { label: 'Medium', cls: 'max-w-3xl' },
  wide: { label: 'Wide', cls: 'max-w-5xl' },
  full: { label: 'Full', cls: 'max-w-none' },
} as const;
type ReadWidth = keyof typeof READ_WIDTHS;

const EXPAND_KEY = 'hs_docs_expanded';
const READW_KEY = 'hs_docs_read_width';

export default function DocsPanel() {
  const { hasRole } = useAuthStore();
  const canEdit = hasRole('super_admin', 'project_admin');
  const [docs, setDocs] = useState<any[]>([]);
  const [selId, setSelId] = useState<string>('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<any | null>(null);

  const [expanded, setExpanded] = useState(
    () => localStorage.getItem(EXPAND_KEY) === '1');
  const [navOpen, setNavOpen] = useState(true);
  const [readWidth, setReadWidth] = useState<ReadWidth>(() => {
    const saved = localStorage.getItem(READW_KEY) as ReadWidth | null;
    return saved && saved in READ_WIDTHS ? saved : 'medium';
  });

  const toggleExpanded = () => setExpanded((v) => {
    localStorage.setItem(EXPAND_KEY, v ? '0' : '1');
    return !v;
  });
  const pickWidth = (w: ReadWidth) => {
    setReadWidth(w);
    localStorage.setItem(READW_KEY, w);
  };

  // Escape leaves the expanded reader rather than closing the whole panel —
  // the smaller undo is almost always the one meant.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); toggleExpanded(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [expanded]);

  const load = () => docsApi.list().then((d) => { setDocs(d); setSelId((cur) => cur || (d[0]?._id ?? '')); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return docs;
    return docs.filter((d) => d.title.toLowerCase().includes(s) || (d.body || '').toLowerCase().includes(s) || (d.tags || []).some((t: string) => t.toLowerCase().includes(s)));
  }, [docs, q]);

  // group filtered -> category -> section -> docs
  const grouped = useMemo(() => {
    const byCat: Record<string, Record<string, any[]>> = {};
    for (const d of filtered) {
      const c = d.category || 'guide'; const s = d.section || '';
      (byCat[c] ||= {}); (byCat[c][s] ||= []).push(d);
    }
    return byCat;
  }, [filtered]);

  const selected = docs.find((d) => d._id === selId) || filtered[0];

  const startNew = () => setEditing({ slug: '', title: '', category: 'guide', section: '', order: 100, body: '# New document\n\n' });
  const saveEdit = async () => {
    try {
      if (editing._id) await docsApi.update(editing._id, editing);
      else await docsApi.create(editing);
      toast.success('Saved'); setEditing(null); load();
    } catch (e: any) { toast.error(e.message || 'Save failed'); }
  };
  const del = async () => { if (!confirm(`Delete "${editing.title}"?`)) return; await docsApi.delete(editing._id).then(() => { setEditing(null); setSelId(''); load(); }).catch((e) => toast.error(e.message)); };

  const nav = (
      <div className={`${expanded ? 'w-64' : 'w-56'} shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col`}>
        <div className="p-2 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search docs…"
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md" />
          </div>
          {canEdit && <button onClick={startNew} className="mt-2 w-full text-xs flex items-center justify-center gap-1 px-2 py-1.5 bg-accent text-white rounded-md"><Plus size={12} /> New doc</button>}
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {CATEGORY_ORDER.filter((c) => grouped[c]).map((c) => (
            <div key={c} className="mb-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 px-1 mb-1">{CATEGORY_LABEL[c] || c}</div>
              {Object.keys(grouped[c]).map((section) => (
                <div key={section} className="mb-1.5">
                  {section && <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 px-1 mt-1">{section}</div>}
                  {grouped[c][section].map((d) => (
                    <button key={d._id} onClick={() => setSelId(d._id)}
                      className={`w-full text-left text-xs px-2 py-1 rounded ${selId === d._id ? 'bg-accent/10 text-accent font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                      {d.title}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ))}
          {filtered.length === 0 && <div className="text-xs text-gray-400 px-1">No matching docs.</div>}
        </div>
      </div>
  );

  const toolbar = (
    <div className="flex items-center gap-1 px-3 h-10 border-b border-gray-200 dark:border-gray-700 shrink-0">
      {expanded && (
        <button
          onClick={() => setNavOpen((v) => !v)}
          title={navOpen ? 'Hide contents' : 'Show contents'}
          className="p-1 rounded text-gray-400 hover:text-accent hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {navOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
        </button>
      )}
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 truncate">
        {selected?.title}
      </span>

      <div className="ml-auto flex items-center gap-1">
        {/* Reading width — only meaningful once there is width to spend. */}
        {expanded && (
          <div className="flex items-center rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden mr-1">
            <span className="px-1.5 text-gray-400"><Type size={12} /></span>
            {(Object.keys(READ_WIDTHS) as ReadWidth[]).map((w) => (
              <button
                key={w}
                onClick={() => pickWidth(w)}
                className={`px-2 py-1 text-[11px] ${readWidth === w
                  ? 'bg-accent/10 text-accent font-medium'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                {READ_WIDTHS[w].label}
              </button>
            ))}
          </div>
        )}
        {canEdit && selected && (
          <button
            onClick={() => setEditing({ ...selected })}
            title="Edit"
            className="p-1 rounded text-gray-400 hover:text-accent hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Pencil size={14} />
          </button>
        )}
        <button
          onClick={toggleExpanded}
          title={expanded ? 'Restore to panel (Esc)' : 'Expand to full page'}
          className="p-1 rounded text-gray-400 hover:text-accent hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </button>
      </div>
    </div>
  );

  const content = (
      <div className="flex-1 flex flex-col min-w-0">
        {toolbar}
        <div className="flex-1 overflow-y-auto">
          {selected ? (
            <div className={`p-5 ${expanded ? READ_WIDTHS[readWidth].cls : 'max-w-3xl'} ${expanded && readWidth !== 'full' ? 'mx-auto' : ''}`}>
              <Markdown body={selected.body} />
              <div className="mt-6 pt-3 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-400">
                Last updated {selected.updatedAt?.slice(0, 10)}{selected.updatedBy && selected.updatedBy !== 'system' ? ` by ${selected.updatedBy}` : ''}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm gap-2"><BookOpen size={28} /> Select a document</div>
          )}
        </div>
      </div>
  );

  const editor = editing ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl p-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{editing._id ? 'Edit document' : 'New document'}</h3>
              <button onClick={() => setEditing(null)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-2">
              <input className={inp + ' col-span-2'} placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              <input className={inp} placeholder="slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
              <input className={inp} type="number" placeholder="order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} />
              <select className={inp + ' col-span-2'} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
              </select>
              <input className={inp + ' col-span-2'} placeholder="section" value={editing.section} onChange={(e) => setEditing({ ...editing, section: e.target.value })} />
            </div>
            <textarea className={inp + ' font-mono text-xs h-72'} placeholder="Markdown…" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
            <div className="flex items-center justify-between mt-2">
              <div>{editing._id && <button onClick={del} className="text-sm flex items-center gap-1 px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50"><Trash2 size={14} /> Delete</button>}</div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(null)} className="text-sm px-3 py-1.5 rounded border border-gray-200 dark:border-gray-600">Cancel</button>
                <button onClick={saveEdit} className="text-sm flex items-center gap-1 px-3 py-1.5 bg-accent text-white rounded-md"><Save size={14} /> Save</button>
              </div>
            </div>
          </div>
        </div>
  ) : null;

  // Expanded: a real full-page reader. `fixed inset-0` deliberately escapes the
  // slideout, because the whole point is not being confined by it.
  if (expanded) {
    return (
      <div className="fixed inset-0 z-40 flex bg-white dark:bg-gray-900">
        {navOpen && nav}
        {content}
        {editor}
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {nav}
      {content}
      {editor}
    </div>
  );
}

const inp = 'w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md';

function Markdown({ body }: { body: string }) {
  return (
    <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...p }) => <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1 mb-3" {...p} />,
          h2: ({ node, ...p }) => <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mt-5 mb-2" {...p} />,
          h3: ({ node, ...p }) => <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-1.5" {...p} />,
          p: ({ node, ...p }) => <p className="my-2" {...p} />,
          ul: ({ node, ...p }) => <ul className="list-disc pl-5 my-2 space-y-1" {...p} />,
          ol: ({ node, ...p }) => <ol className="list-decimal pl-5 my-2 space-y-1" {...p} />,
          li: ({ node, ...p }) => <li className="marker:text-gray-400" {...p} />,
          a: ({ node, ...p }) => <a className="text-accent hover:underline" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-semibold text-gray-900 dark:text-gray-100" {...p} />,
          blockquote: ({ node, ...p }) => <blockquote className="border-l-2 border-accent/40 pl-3 my-3 text-gray-500 dark:text-gray-400 italic" {...p} />,
          hr: () => <hr className="my-4 border-gray-100 dark:border-gray-800" />,
          code: ({ node, className, children, ...p }: any) => {
            // A ```mermaid fence becomes a rendered diagram rather than its own
            // source. Everything else stays a code block.
            if (className === 'language-mermaid') {
              return <Mermaid chart={String(children).trimEnd()} />;
            }
            const inline = !className;
            return inline
              ? <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[12px] font-mono text-pink-600 dark:text-pink-300" {...p}>{children}</code>
              : <code className="font-mono text-[12px]" {...p}>{children}</code>;
          },
          pre: ({ node, children, ...p }: any) => {
            // react-markdown wraps fenced code in <pre>. For a mermaid fence the
            // child is already a rendered diagram, and nesting it in <pre> gives
            // the SVG monospace sizing and a scrollbar it does not want.
            const child: any = Array.isArray(children) ? children[0] : children;
            if (child?.props?.className === 'language-mermaid') return <>{children}</>;
            return <pre className="my-3 p-3 rounded-md bg-gray-900 text-gray-100 overflow-x-auto text-[12px]" {...p}>{children}</pre>;
          },
          table: ({ node, ...p }) => <div className="my-3 overflow-x-auto"><table className="w-full text-xs border-collapse" {...p} /></div>,
          th: ({ node, ...p }) => <th className="border border-gray-200 dark:border-gray-700 px-2 py-1 text-left bg-gray-50 dark:bg-gray-800 font-semibold" {...p} />,
          td: ({ node, ...p }) => <td className="border border-gray-200 dark:border-gray-700 px-2 py-1 align-top" {...p} />,
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}
